import { NavLink, Link, useLocation } from "react-router-dom";
import {
  House,
  MagnifyingGlass,
  Compass,
  ChatCircle,
  Bell,
  PlusSquare,
  User,
  Fire,
  SignOut,
  Broadcast,
  DoorOpen,
  Storefront,
  ChartLineUp,
  Trophy,
  Graph,
  Newspaper,
  Sun,
  Moon,
  Gear,
  ShieldStar,
} from "@phosphor-icons/react";
import { currentUser } from "@/data/mockData";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useWs } from "@/context/WebSocketContext";
import { useBrand } from "@/context/BrandContext";

const nav = [
  { to: "/", label: "Feed", icon: House, end: true },
  { to: "/boletin", label: "Boletín", icon: Newspaper, badge: "N°14" },
  { to: "/explorar", label: "Directorio", icon: Compass },
  { to: "/colaboraciones", label: "Colabs", icon: Graph, dot: true },
  { to: "/live", label: "Live Arte", icon: Broadcast, live: true },
  { to: "/salas", label: "Salas 24/7", icon: DoorOpen, dot: true },
  { to: "/convocatorias", label: "Convocatorias", icon: Trophy, badge: "12" },
  { to: "/tienda", label: "Tienda", icon: Storefront },
  { to: "/estudio", label: "Mi estudio", icon: ChartLineUp, badge: "€" },
  { to: "/buscar", label: "Buscar", icon: MagnifyingGlass },
  { to: "/disciplinas", label: "Disciplinas", icon: Fire },
  { to: "/mensajes", label: "Mensajes", icon: ChatCircle, badgeKey: "messages" },
  { to: "/notificaciones", label: "Notifi.", icon: Bell, badgeKey: "notifs" },
  { to: `/artista/${currentUser.slug}`, label: "Perfil", icon: User },
];

export default function Sidebar() {
  const { pathname } = useLocation();
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { unreadNotifs, unreadMessages } = useWs();
  const { settings } = useBrand();
  const siteName = settings?.site_name || "ELARTE/NET";
  const logoUrl = settings?.logo_url;
  const logoSrc = logoUrl && logoUrl.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`
    : logoUrl;
  const isAdmin = user && typeof user === "object" && user.role === "admin";
  const isEditorial = theme === "editorial";
  const me = user && typeof user === "object"
    ? {
        slug: user.handle,
        name: user.name,
        handle: user.handle,
        avatar: user.photo,
      }
    : null;
  const nav_me = me || currentUser;
  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex fixed inset-y-0 left-0 w-[240px] lg:w-[260px] border-r border-[#27272A] bg-[#050505] flex-col z-40"
    >
      {/* Logo */}
      <Link to="/" className="px-6 py-7 border-b border-[#27272A] block">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#FF3E00] mb-1">
          {(settings?.tagline || "SOMOS · EL · ARTE").toUpperCase()}
        </div>
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={siteName}
            className="max-h-10 max-w-full object-contain"
          />
        ) : (
          <div className="font-display text-2xl font-black tracking-tighter uppercase leading-none">
            {(() => {
              const parts = siteName.split(/([/])/);
              return parts.map((p, i) =>
                p === "/" ? (
                  <span key={i} className="text-[#FF3E00]">{p}</span>
                ) : (
                  <span key={i}>{p}</span>
                )
              );
            })()}
          </div>
        )}
      </Link>

      {/* Nav */}
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-5 flex flex-col gap-1 sidebar-scroll">
        {nav.map((n) => {
          const Icon = n.icon;
          const active = n.end ? pathname === n.to : pathname.startsWith(n.to);
          let badge = n.badge;
          if (n.badgeKey === "messages") badge = unreadMessages > 0 ? unreadMessages : null;
          else if (n.badgeKey === "notifs") badge = unreadNotifs > 0 ? unreadNotifs : null;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`sidebar-nav-${n.label.toLowerCase()}`}
              className={`group flex items-center gap-4 px-3 py-3 border border-transparent transition-all ${
                active
                  ? "bg-[#FF3E00] text-white border-[#FF3E00]"
                  : "text-[#A1A1AA] hover:text-white hover:border-[#27272A]"
              }`}
            >
              <Icon size={22} weight={active ? "fill" : "regular"} />
              <span className="font-display font-semibold tracking-tight text-[15px]">
                {n.label}
              </span>
              {n.badge ? (
                <span
                  className={`ml-auto font-mono text-[10px] px-1.5 py-0.5 ${
                    active
                      ? "bg-black text-[#FF3E00]"
                      : "bg-[#FF3E00] text-white"
                  }`}
                >
                  {n.badge}
                </span>
              ) : n.live ? (
                <span className="ml-auto inline-flex items-center gap-1 font-mono text-[9px] tracking-[0.2em] uppercase">
                  <span className="h-1.5 w-1.5 bg-[#FF0033] live-dot" />
                  <span className={active ? "text-white" : "text-[#FF0033]"}>LIVE</span>
                </span>
              ) : n.dot ? (
                <span className="ml-auto inline-flex items-center gap-1 font-mono text-[9px] tracking-[0.2em] uppercase">
                  <span className="h-1.5 w-1.5 bg-[#00FF66] live-dot" />
                  <span className={active ? "text-white" : "text-[#00FF66]"}>24/7</span>
                </span>
              ) : null}
            </NavLink>
          );
        })}
        {isAdmin ? (
          <NavLink
            to="/admin"
            data-testid="sidebar-admin-link"
            className={({ isActive }) =>
              `mt-3 flex items-center gap-3 px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
                isActive
                  ? "bg-[#FF3E00] text-white"
                  : "border border-[#FF3E00] text-[#FF3E00] hover:bg-[#FF3E00] hover:text-white"
              }`
            }
          >
            <ShieldStar size={14} weight="fill" />
            Superadmin
          </NavLink>
        ) : null}
      </nav>

      {/* Publicar CTA */}
      <div className="p-4 border-t border-[#27272A] space-y-2">
        <Link
          to="/publicar"
          data-testid="sidebar-publish-btn"
          className="btn-brutal flex items-center justify-center gap-2 w-full bg-[#FF3E00] text-white py-3 font-mono uppercase text-xs tracking-[0.2em] font-bold"
        >
          <PlusSquare size={16} weight="bold" /> Publicar
        </Link>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            className="flex items-center justify-center gap-2 border border-[#27272A] hover:border-[#FF3E00] py-2 font-mono uppercase text-[10px] tracking-[0.2em] text-[#A1A1AA] hover:text-[#FF3E00]"
            title="Cambiar tema"
          >
            {isEditorial ? <Moon size={12} weight="fill" /> : <Sun size={12} weight="fill" />}
            {isEditorial ? "Dark" : "Light"}
          </button>
          <Link
            to="/ajustes"
            data-testid="sidebar-settings"
            className="flex items-center justify-center gap-2 border border-[#27272A] hover:border-[#F4F4F5] py-2 font-mono uppercase text-[10px] tracking-[0.2em] text-[#A1A1AA] hover:text-white"
          >
            <Gear size={12} />
            Ajustes
          </Link>
        </div>
      </div>

      {/* Account */}
      {me ? (
        <div className="flex items-center gap-3 p-4 border-t border-[#27272A]">
          <Link
            to={`/artista/${nav_me.slug || nav_me.handle}`}
            className="flex items-center gap-3 flex-1 min-w-0 hover:bg-[#121212] transition-colors -m-1 p-1"
          >
            <div className="h-10 w-10 overflow-hidden border border-[#27272A] bg-[#121212] shrink-0">
              {nav_me.avatar ? (
                <img
                  src={nav_me.avatar}
                  alt={nav_me.name}
                  className="w-full h-full object-cover"
                />
              ) : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{nav_me.name}</div>
              <div className="font-mono text-[10px] text-[#A1A1AA] truncate">
                @{nav_me.handle}
              </div>
            </div>
          </Link>
          <button
            data-testid="sidebar-logout"
            onClick={logout}
            title="Cerrar sesión"
            className="h-10 w-10 inline-flex items-center justify-center text-[#A1A1AA] hover:text-[#FF0033]"
          >
            <SignOut size={16} />
          </button>
        </div>
      ) : (
        <div className="p-4 border-t border-[#27272A] grid grid-cols-2 gap-2">
          <Link
            to="/ingresar"
            data-testid="sidebar-login"
            className="font-mono uppercase text-[10px] tracking-[0.22em] border border-[#27272A] hover:border-[#F4F4F5] text-[#A1A1AA] hover:text-white py-2 text-center"
          >
            Ingresar
          </Link>
          <Link
            to="/registro"
            data-testid="sidebar-register"
            className="font-mono uppercase text-[10px] tracking-[0.22em] bg-[#FF3E00] text-white py-2 text-center font-bold"
          >
            Registrar
          </Link>
        </div>
      )}
    </aside>
  );
}
